import React, { useState, useEffect } from "react";
import TextBox2 from "../TextBox2";
import edit from "../../icons/Edit.svg";
import back from "../../icons/left.png";
import { useDispatch, useSelector } from "react-redux";
import { updateHostProfile } from "../../store/thunks/hostActions";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import EntryInstructionEmailPreference from "../onboarding/EntryInstructionEmailPreference";

function ProfileComponent({ Info }) {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const { hid } = params;

  const [editMode, setEditMode] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);

  const {
    loading: loadingUpdate,
    success: successUpdate,
    error: errorUpdate,
  } = useSelector((state) => state.hostUpdate);

  const [address, setAddress] = useState({
    line1: "",
    line2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const [credentials, setCredentials] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
  });

  const handleUpdate = () => {
    const profileCredentials = { address, ...credentials };
    dispatch(updateHostProfile({ profileCredentials, hid }));
  };

  useEffect(() => {
    if (errorUpdate) {
      setEditMode(true);
      setIsReadOnly(false);
    } else if (successUpdate) {
      setEditMode(false);
      setIsReadOnly(true);
    }
    if (Info) {
      setAddress({ ...Info.address });
      setCredentials({
        firstName: Info.firstName,
        lastName: Info.lastName,
        email: Info.email,
        phoneNumber: Info.phoneNumber,
        password: "",
      });
    }
  }, [Info, dispatch, errorUpdate, successUpdate]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-xl text-gray-500 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
            aria-label="Go back"
          >
            <img src={back} alt="" className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Profile Settings</h1>
        </div>
        <div className="flex items-center gap-2">
          {loadingUpdate && (
            <CircularProgress color="inherit" className="mr-2" size={20} />
          )}
          {!editMode ? (
            <button
              type="button"
              onClick={() => {
                setEditMode(true);
                setIsReadOnly(false);
              }}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-green-700 rounded-xl border border-green-200/60 hover:border-green-400 hover:bg-green-50/50 transition-all duration-300 shadow-sm hover:shadow-md font-medium text-sm"
            >
              <img src={edit} alt="" className="w-4 h-4 opacity-90 [filter:brightness(0)_saturate(100%)_invert(0.45)_sepia(1)_saturate(4)_hue-rotate(95deg)]" />
              <span className="hidden sm:inline">Edit Profile</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleUpdate}
              className="px-5 py-2.5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-md shadow-primary-500/25 font-medium text-sm"
            >
              Update
            </button>
          )}
        </div>
      </div>

      {/* Personal Info Card */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden hover:shadow-lg transition-all duration-300">
        <div className="p-6 border-b border-gray-100/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Personal Information</h3>
              <p className="text-sm text-gray-500 mt-0.5">Your name, email, and contact details</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TextBox2 label="First Name" name="firstName" type="text" credentials={credentials} value={credentials.firstName} setValue={setCredentials} readOnly={isReadOnly} />
            <TextBox2 label="Last Name" name="lastName" type="text" credentials={credentials} value={credentials.lastName} setValue={setCredentials} readOnly={isReadOnly} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <TextBox2 label="Email" name="email" type="email" credentials={credentials} value={credentials.email} setValue={setCredentials} readOnly disabled={editMode} />
              <Link to={`/settings/${hid}/change-email`} className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                Change Email
              </Link>
            </div>
            <TextBox2 label="Phone" name="phoneNumber" type="tel" credentials={credentials} value={credentials.phoneNumber} setValue={setCredentials} readOnly={isReadOnly} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TextBox2 label="Company Name" name="companyName" type="text" credentials={credentials} readOnly={isReadOnly} />
            <div className="space-y-1.5">
              <TextBox2 label="Password" name="password" type="text" credentials={credentials} value="*************" setValue={setCredentials} readOnly />
              <Link to={`/settings/${hid}/reset-password`} className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors">
                Change Password
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Address Card */}
      <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/40 overflow-hidden hover:shadow-lg transition-all duration-300">
        <div className="p-6 border-b border-gray-100/50">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Address</h3>
              <p className="text-sm text-gray-500 mt-0.5">Your billing or mailing address</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <TextBox2 label="Address Line 1" name="line1" type="text" credentials={address} value={address.line1} setValue={setAddress} readOnly={isReadOnly} />
            <TextBox2 label="Address Line 2" name="line2" type="text" credentials={address} value={address.line2} setValue={setAddress} readOnly={isReadOnly} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <TextBox2 label="City" name="city" type="text" credentials={address} value={address.city} setValue={setAddress} readOnly={isReadOnly} />
            <TextBox2 label="State" name="state" type="text" credentials={address} value={address.state} setValue={setAddress} readOnly={isReadOnly} />
            <TextBox2 label="Zip Code" name="zipCode" type="text" credentials={address} value={address.zipCode} setValue={setAddress} readOnly={isReadOnly} />
          </div>
        </div>
      </div>

      <EntryInstructionEmailPreference
        hostId={hid}
        title="Entry instruction emails"
        description="Get notified when orders need entry instructions or delivery timing context."
        footerLink={{ href: "/integrations", label: "Integrations" }}
        className="bg-white/70 shadow-sm transition-all duration-300 hover:shadow-lg"
      />
    </div>
  );
}

export default ProfileComponent;
